import { useCallback, useContext, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { io, Socket } from 'socket.io-client';
import { config } from '../../../config/config';
import { getStoredIdToken } from '../../../authToken';
import { globalContext } from '../../../Provider/AppProvider';
import ToasterWithMessage from '../../../components/shared-components/toaster/index';
import { installChimeUnlock, playNotificationChime } from '../../../utils/notificationChime';
import { GET_VL_SUPPORT_UNREAD } from '../api/supportTicketsQuery';
import { installDesktopNotificationPrompt, showDesktopNotification } from '../../../utils/desktopNotification';
import { summarizeSupportAlerts, type SupportAlertTicket } from '../supportTicketUtils';

export const VL_SUPPORT_UPDATED_EVENT = 'vl-support-updated';
const POLL_MS = 15_000;

export type VlSupportSocketUpdate = {
  ticketId: string;
  kind: 'opened' | 'reply_received' | 'reply_sent' | 'completed' | 'reopened' | 'read';
  subject?: string | null;
  eventName?: string | null;
  ownerInitials?: string | null;
  fromName?: string | null;
  at?: string;
};

const listeners = new Set<(update: VlSupportSocketUpdate) => void>();

/** Lets pages (Support Tickets, Listed cards) refresh when a ticket changes. */
export function subscribeVlSupportUpdates(listener: (update: VlSupportSocketUpdate) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Counts unread replies (not just tickets with one), so a second reply on a ticket still alerts.
const SEEN_KEY = 'vlSupportUnreadReplies';

/** Last unread count this browser alerted on, so replies that arrive while the tab is closed still chime. */
export const readSeenUnread = (): number | null => {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    return raw === null ? null : Number(raw);
  } catch {
    return null;
  }
};

const writeSeenUnread = (count: number) => {
  try {
    window.localStorage.setItem(SEEN_KEY, String(count));
  } catch {
    // Storage blocked: alerts still work within this tab.
  }
};

/** Pure: should this unread-count change alert the team? (exported for tests) */
export const isNewReply = (previous: number | null, next: number): boolean => previous !== null && next > previous;

/**
 * Mounted once in the dashboard layout. Keeps the sidebar unread badge current,
 * and when Victory Live replies plays the chime and shows a toast for everyone
 * logged in. Socket events give instant updates; a 30s poll covers dropped sockets.
 */
export function useVlSupportAlerts(enabled = true) {
  const apolloClient = useApolloClient();
  const { handleGlobalState } = useContext<any>(globalContext);
  const { info } = ToasterWithMessage();
  // Start from what this browser last saw, so replies that came in while it was closed or offline still alert.
  const lastCountRef = useRef<number | null>(readSeenUnread());
  const lastReplyRef = useRef<VlSupportSocketUpdate | null>(null);
  const infoRef = useRef(info);
  infoRef.current = info;

  const refresh = useCallback(async () => {
    try {
      const { data } = await apolloClient.query({ query: GET_VL_SUPPORT_UNREAD, fetchPolicy: 'network-only' });
      const { needsReplyCount: count, unreadReplies: replies, latest } = summarizeSupportAlerts(
        (data?.vl_support_ticket || []) as SupportAlertTicket[],
      );
      if (isNewReply(lastCountRef.current, replies)) {
        playNotificationChime();
        const reply = lastReplyRef.current;
        // Tickets Victory Live started have no event yet, so the email's subject stands in for it.
        const eventName = reply?.eventName || latest?.vst_event_name || reply?.subject || latest?.vst_subject || null;
        const initials = reply?.ownerInitials || latest?.vst_owner_initials || null;
        const ticketId = reply?.ticketId || latest?.vst_id || null;
        const message = eventName
          ? `Victory Live replied: ${eventName}${initials ? ` (${initials})` : ''}`
          : 'Victory Live replied to a support ticket';
        infoRef.current(message);
        // Shows even when the person is in another app, and needs no click on this page first.
        showDesktopNotification({
          title: 'Victory Live replied',
          body: eventName ? `${eventName}${initials ? ` (${initials})` : ''}` : 'A support ticket needs a reply',
          tag: 'vl-support-reply',
          onClick: () => {
            const url = `/support-tickets${ticketId ? `?ticket=${encodeURIComponent(ticketId)}` : ''}`;
            window.history.pushState({}, '', url);
            window.dispatchEvent(new PopStateEvent('popstate'));
          },
        });
      }
      lastCountRef.current = replies;
      writeSeenUnread(replies);
      lastReplyRef.current = null;
      handleGlobalState((prev: any) => (prev?.vlSupportUnread === count ? prev : { ...prev, vlSupportUnread: count }));
    } catch {
      // Table not available on this Hasura yet — leave the badge hidden.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient]);

  useEffect(() => {
    if (!enabled) return;
    installChimeUnlock();
    installDesktopNotificationPrompt();
    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    // Browsers slow timers in background tabs, so check again the moment the person comes back.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    const url = String(process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    const token = getStoredIdToken();
    let socket: Socket | null = null;
    if (url && token) {
      socket = io(url, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        auth: { token },
        reconnection: true,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 15000,
      });
      socket.on(VL_SUPPORT_UPDATED_EVENT, (update: VlSupportSocketUpdate) => {
        if (update?.kind === 'reply_received') lastReplyRef.current = update;
        listeners.forEach((listener) => listener(update));
        void refresh();
      });
    }
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      socket?.disconnect();
      socket = null;
    };
  }, [enabled, refresh]);

  return { refresh };
}
